import ensureError from "@-/com.npmjs.ensure-error";
import { type PaywallInfo, usePlacement, useSuperwall } from "@-/com.npmjs.expo-superwall";
import { Alert, Platform } from "@-/com.npmjs.react-native";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useCallback, useRef, useState } from "react";

const PAYWALL_UNAVAILABLE_MESSAGE = "Premium plans could not be loaded. Please try again shortly.";

/**
 * Whether Superwall presented `paywallInfo` without its store products.
 *
 * Superwall still presents a paywall when the store returns none of its products: it only tracks
 * `paywallProductsLoad_missingProducts`, and the paywall renders empty prices, a "NaN% OFF" badge
 * and a Continue button that cannot buy anything. Staging hit this on 2026-10-10, when StoreKit
 * returned neither subscription after the Apple Developer membership lapsed and took the Paid Apps
 * Agreement with it.
 *
 * The SDK stamps `productsLoadFailTime` only when the product fetch failed. Despite the `number`
 * typing, iOS bridges SuperwallKit's ISO string, which is empty after a successful fetch; Android
 * omits the field on success.
 */
export function didPaywallProductsFailToLoad(
  paywallInfo: Pick<PaywallInfo, "productIds" | "productsLoadFailTime">,
): boolean {
  if (paywallInfo.productIds.length === 0) {
    return false;
  }
  const failTime: unknown = paywallInfo.productsLoadFailTime;
  return typeof failTime === "number" || (typeof failTime === "string" && failTime !== "");
}

/**
 * `onUnavailable` for entry points with no inline slot for `errorMessage`, so a paywall that
 * cannot be shown is never a silent no-op.
 */
export function alertPremiumUnavailable(message: string): void {
  Alert.alert("Premium unavailable", message);
}

/**
 * Opens the Superwall paywall for `placement`. `source` names the UI entry point (several share
 * one placement); it is sent with every paywall analytics event and as a placement param, so both
 * PostHog and Superwall can tell the entry points apart.
 *
 * A paywall that Superwall presents without its store products is dismissed at once and reported
 * like a failed presentation: its prices are blank and its purchase button cannot buy anything.
 */
export function useUpgradeToPremium(
  placement: string,
  source: string,
  {
    onUnavailable,
  }: {
    /** Called with the message whenever the paywall cannot be shown (see `alertPremiumUnavailable`). */
    onUnavailable?: (message: string) => void;
  } = {},
): {
  errorMessage: string | null;
  isLoading: boolean;
  isPaywallPresented: boolean;
  openUpgradeToPremium: () => void;
} {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isOpeningRef = useRef(false);
  const dismissPaywall = useSuperwall((state) => state.dismiss);
  const finishOpening = useCallback(() => {
    isOpeningRef.current = false;
    setIsLoading(false);
  }, []);
  const reportUnavailable = useCallback(
    (message: string) => {
      setErrorMessage(message);
      onUnavailable?.(message);
    },
    [onUnavailable],
  );
  const { registerPlacement, state } = usePlacement({
    onDismiss: finishOpening,
    onError(error) {
      finishOpening();
      captureAnalyticsEvent("paywall_failed", { placement, reason: "presentation_error", source });
      reportUnavailable(PAYWALL_UNAVAILABLE_MESSAGE);
      logger(import.meta.location).error("Superwall paywall presentation failed", error);
    },
    onPresent(paywallInfo) {
      finishOpening();
      if (!didPaywallProductsFailToLoad(paywallInfo)) {
        return;
      }
      captureAnalyticsEvent("paywall_failed", {
        placement,
        reason: "products_unavailable",
        source,
      });
      reportUnavailable(PAYWALL_UNAVAILABLE_MESSAGE);
      logger(import.meta.location).error("Superwall presented a paywall without its products", {
        paywall: paywallInfo.identifier,
        placement,
        productIds: paywallInfo.productIds,
      });
      void dismissPaywall().catch((error: unknown) => {
        logger(import.meta.location).error(
          "Superwall paywall without products could not be dismissed",
          ensureError(error),
        );
      });
    },
    onSkip(reason) {
      finishOpening();
      captureAnalyticsEvent("paywall_skipped", { placement, reason: reason.type, source });
      reportUnavailable(PAYWALL_UNAVAILABLE_MESSAGE);
      logger(import.meta.location).warn("Superwall paywall presentation skipped", {
        placement,
        reason: reason.type,
      });
    },
  });

  const openUpgradeToPremium = useCallback((): void => {
    if (isOpeningRef.current || state.status === "presented") {
      return;
    }
    captureAnalyticsEvent("paywall_requested", { placement, source });
    if (Platform.OS === "web") {
      reportUnavailable("Premium upgrades are available in the Haniverse mobile app.");
      return;
    }
    setErrorMessage(null);
    isOpeningRef.current = true;
    setIsLoading(true);
    void registerPlacement({ params: { source }, placement })
      .then(finishOpening)
      .catch((error: unknown) => {
        finishOpening();
        captureAnalyticsEvent("paywall_failed", {
          placement,
          reason: "registration_error",
          source,
        });
        reportUnavailable(PAYWALL_UNAVAILABLE_MESSAGE);
        logger(import.meta.location).error(
          "Superwall placement registration failed",
          ensureError(error),
        );
      });
  }, [finishOpening, placement, registerPlacement, reportUnavailable, source, state.status]);

  return {
    errorMessage,
    isLoading,
    isPaywallPresented: state.status === "presented",
    openUpgradeToPremium,
  };
}
